import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';

export interface SearchFilters {
  status?: string;
  type?: string;
  dateRange?: {
    start?: Date;
    end?: Date;
  };
}

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onFilterChange?: (filters: SearchFilters) => void;
  placeholder?: string;
  showFilters?: boolean;
  className?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onFilterChange,
  placeholder = 'Search cases, documents...',
  showFilters = true,
  className = '',
  onFocus,
  onBlur,
}) => {
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const filterFieldIds = {
    status: 'search-filter-status',
    type: 'search-filter-type',
    dateStart: 'search-filter-date-start',
    dateEnd: 'search-filter-date-end',
  };

  // Close filter menu when clicking outside
  useEffect(() => {
    if (!showFilterMenu) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('[data-filter-button]')
      ) {
        setShowFilterMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterMenu]);

  const handleFilterChange = (key: keyof SearchFilters, filterValue: any) => {
    const newFilters = { ...filters, [key]: filterValue };
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters: SearchFilters = {};
    setFilters(clearedFilters);
    onFilterChange?.(clearedFilters);
    toast.info('Filters cleared');
  };

  const hasActiveFilters = filters.status || filters.type || filters.dateRange;

  return (
    <div className={`relative ${className}`}>
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-luna-text-light/50 dark:text-luna-text-dark/50 text-lg pointer-events-none">
            search
          </span>
          <input
            ref={inputRef}
            type="search"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-luna-primary border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg text-sm text-luna-text-light dark:text-luna-text-dark placeholder:text-luna-text-light/40 dark:placeholder:text-luna-text-dark/40 focus:outline-none focus:ring-2 focus:ring-luna-secondary/50 focus:border-luna-secondary transition-all"
          />
          {value && (
            <button
              onClick={() => {
                onChange('');
                inputRef.current?.focus();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-luna-text-light/10 dark:hover:bg-luna-text-dark/10 transition-colors"
              aria-label="Clear search"
            >
              <span className="material-symbols-outlined text-base text-luna-text-light/60 dark:text-luna-text-dark/60">
                close
              </span>
            </button>
          )}
        </div>

        {showFilters && (
          <>
            <button
              data-filter-button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className={`flex items-center gap-2 px-3 py-2.5 border rounded-lg text-sm font-medium transition-colors ${
                hasActiveFilters
                  ? 'bg-luna-secondary/10 border-luna-secondary text-luna-secondary'
                  : 'bg-white dark:bg-luna-primary border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light dark:text-luna-text-dark hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark'
              }`}
              aria-label="Filter options"
            >
              <span className="material-symbols-outlined text-base">tune</span>
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-luna-secondary"></span>
              )}
            </button>

            {showFilterMenu && (
              <div
                ref={filterMenuRef}
                className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-luna-primary border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg shadow-lg p-4 z-50"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-luna-text-light dark:text-luna-text-dark">
                    Filters
                  </h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-luna-secondary hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Status Filter */}
                  <div>
                    <label
                      htmlFor={filterFieldIds.status}
                      className="block text-xs font-medium text-luna-text-light/70 dark:text-luna-text-dark/70 mb-2"
                    >
                      Status
                    </label>
                    <select
                      id={filterFieldIds.status}
                      value={filters.status || ''}
                      onChange={(e) =>
                        handleFilterChange('status', e.target.value || undefined)
                      }
                      className="w-full px-3 py-2 bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg text-sm text-luna-text-light dark:text-luna-text-dark focus:outline-none focus:ring-2 focus:ring-luna-secondary/50"
                    >
                      <option value="">All statuses</option>
                      <option value="Active">Active</option>
                      <option value="Pending Review">Pending Review</option>
                      <option value="Closed">Closed</option>
                    </select>
                  </div>

                  {/* Type Filter */}
                  <div>
                    <label
                      htmlFor={filterFieldIds.type}
                      className="block text-xs font-medium text-luna-text-light/70 dark:text-luna-text-dark/70 mb-2"
                    >
                      Type
                    </label>
                    <select
                      id={filterFieldIds.type}
                      value={filters.type || ''}
                      onChange={(e) =>
                        handleFilterChange('type', e.target.value || undefined)
                      }
                      className="w-full px-3 py-2 bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg text-sm text-luna-text-light dark:text-luna-text-dark focus:outline-none focus:ring-2 focus:ring-luna-secondary/50"
                    >
                      <option value="">All types</option>
                      <option value="Contract">Contract</option>
                      <option value="Litigation">Litigation</option>
                      <option value="Consultation">Consultation</option>
                      <option value="Compliance">Compliance</option>
                    </select>
                  </div>

                  {/* Date Range Filter */}
                  <fieldset className="space-y-2">
                    <legend className="text-xs font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">
                      Created Date
                    </legend>
                    <div className="flex gap-2">
                      <input
                        id={filterFieldIds.dateStart}
                        type="date"
                        value={
                          filters.dateRange?.start
                            ? filters.dateRange.start.toISOString().split('T')[0]
                            : ''
                        }
                        onChange={(e) =>
                          handleFilterChange('dateRange', {
                            ...filters.dateRange,
                            start: e.target.value ? new Date(e.target.value) : undefined,
                          })
                        }
                        className="flex-1 px-3 py-2 bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg text-sm text-luna-text-light dark:text-luna-text-dark focus:outline-none focus:ring-2 focus:ring-luna-secondary/50"
                      />
                      <input
                        id={filterFieldIds.dateEnd}
                        type="date"
                        value={
                          filters.dateRange?.end
                            ? filters.dateRange.end.toISOString().split('T')[0]
                            : ''
                        }
                        onChange={(e) =>
                          handleFilterChange('dateRange', {
                            ...filters.dateRange,
                            end: e.target.value ? new Date(e.target.value) : undefined,
                          })
                        }
                        className="flex-1 px-3 py-2 bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg text-sm text-luna-text-light dark:text-luna-text-dark focus:outline-none focus:ring-2 focus:ring-luna-secondary/50"
                      />
                    </div>
                  </fieldset>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchBar;

